import localforage from 'localforage';
import { ref } from 'vue';
import { defineStore } from 'pinia';

import type {
  Calculation,
  Client,
  SegmentItemProps,
  Segment,
  CalculationSegment,
} from '../utils/types';
import { generateUniqueId } from '../utils/uuid';
import { calculationService } from '../utils/api/calculationService';

export const useCalculationsStore = defineStore(
  'calculations',
  () => {
    const data = ref<Calculation[] | null>(null);

    async function getCalculationsFromLocaldorage() {
      await localforage.getItem<Calculation[]>('calculations');
    }

    async function fetch() {
      await getCalculationsFromLocaldorage();

      if (data.value) return;

      const fetched = await calculationService.getCalculations();

      data.value = flattenCalculations(fetched);
    }

    function flattenCalculations(fetched: Calculation[] | null | undefined): Calculation[] {
      if (!fetched) return [];

      const flattened: Calculation[] = [];

      fetched.forEach((calculation) => {
        const { calculation_children: children, ...rest } = calculation as Calculation & {
          calculation_children?: Calculation[];
        };

        flattened.push(rest as Calculation);

        if (Array.isArray(children)) {
          children.forEach((child) => flattened.push(child));
        }
      });

      return flattened;
    }

    function invalidate() {
      data.value = null;

      fetch();
    }

    function addNewCalculation(client: Client, name: string, calculatorId: number) {
      const previousCalculations = data.value || [];

      const newCalculation = {
        id: null,
        uuid: generateUniqueId(),
        client_id: client.id,
        client_uuid: client.uuid,
        name,
        calculation_segments: [],
        calculator_id: calculatorId,
        calculation_created_at: new Date().toISOString(),
      };

      data.value = [newCalculation, ...previousCalculations];

      return newCalculation;
    }

    function appendCalculation(calculation: Calculation) {
      const previousCalculations = data.value || [];

      data.value = [calculation, ...previousCalculations];
    }

    function getCalculationByUuid(uuid: string) {
      const currentCalculation = data.value?.find((calculation) => calculation.uuid === uuid);

      if (!currentCalculation) {
        return null;
      }

      return currentCalculation;
    }

    function addSegmentToCalculation(uuid: string, segment: Segment) {
      const currentCalculation = getCalculationByUuid(uuid);

      if (!currentCalculation || !data.value) {
        throw Error('Brak kalkulacji o podanym uuid');
      }

      data.value = data.value.map((calculation) => {
        if (calculation.uuid === currentCalculation.uuid) {
          const segmentUuid = generateUniqueId();

          return {
            ...calculation,
            calculation_segments: [
              ...calculation.calculation_segments,
              {
                id: null,
                uuid: segmentUuid,
                calculation_id: calculation.id,
                calculation_uuid: calculation.uuid,
                segment_id: segment.id,
                calculation_segment_items: segment.view_groups
                  .map((group) => group.views)
                  .flat()
                  .map((item) => item.items)
                  .flat()
                  .map((item) => {
                    if (item.type === 'value') {
                      return {
                        value: item.value,
                        uuid: generateUniqueId(),
                        type: item.type,
                        item_id: item.id,
                        calculation_segment_uuid: segmentUuid,
                        calculation_segment_id: segment.id,
                      };
                    }

                    if (item.type === 'product') {
                      return {
                        uuid: generateUniqueId(),
                        type: item.type,
                        product_id: item.products.filter(
                          (product) => !product.is_archived && product.is_available
                        )[0].id,
                        product_price_id: item.products.filter(
                          (product) => !product.is_archived && product.is_available
                        )[0].product_price.id,
                        item_id: item.id,
                        calculation_segment_uuid: segmentUuid,
                        calculation_segment_id: segment.id,
                      };
                    }

                    if (item.type === 'rule') {
                      return {
                        uuid: generateUniqueId(),
                        type: item.type,
                        rule_id: item.rule_id,
                        item_id: item.id,
                        calculation_segment_uuid: segmentUuid,
                        calculation_segment_id: segment.id,
                      };
                    }

                    throw new Error(`Unsupported item type: ${(item as any).type}`);
                  }),
              },
            ],
          };
        }

        return calculation;
      });
    }

    function copySegmentToCalculation(
      calculationUuid: string,
      lastCalculationSegment: CalculationSegment,
      segment: Segment
    ) {
      const currentCalculation = getCalculationByUuid(calculationUuid);

      if (!currentCalculation || !data.value) {
        throw Error('Brak kalkulacji o podanym uuid');
      }

      const segmentUuid = generateUniqueId();

      addSegmentToCalculation(calculationUuid, segment);

      data.value = data.value.map((calculation) => {
        if (calculation.uuid === currentCalculation.uuid) {
          return {
            ...calculation,
            calculation_segments: calculation.calculation_segments.map((calculationSegment) => {
              if (calculationSegment.segment_id === lastCalculationSegment.segment_id) {
                return {
                  ...calculationSegment,
                  calculation_segment_items: calculationSegment.calculation_segment_items.map(
                    (item) => {
                      const lastItem = lastCalculationSegment.calculation_segment_items.find(
                        (lt) => lt.item_id === item.item_id
                      );

                      if (item.type === 'value') {
                        return {
                          ...item,
                          value: lastItem?.type === 'value' ? lastItem.value : item.value,
                        };
                      }

                      if (item.type === 'product') {
                        return {
                          ...item,
                          product_id:
                            lastItem?.type === 'product' ? lastItem.product_id : item.product_id,
                        };
                      }

                      if (item.type === 'rule') {
                        return {
                          ...item,
                          rule_id: lastItem?.type === 'rule' ? lastItem.rule_id : item.rule_id,
                        };
                      }

                      return item as any;
                    }
                  ),
                };
              }

              return calculationSegment;
            }),
          };
        }

        return calculation;
      });
    }

    function getUpdatedItemsCalculationSegment(
      uuid: string,
      segmentId: number,
      items: SegmentItemProps[]
    ) {
      const currentCalculation = getCalculationByUuid(uuid);

      if (!currentCalculation || !data.value) {
        throw Error('Brak kalkulacji o podanym uuid');
      }

      const segments = currentCalculation.calculation_segments;

      const updatedSegments = segments.map((segment) => {
        if (segment.segment_id === segmentId) {
          const calculationSegmentItems = segment.calculation_segment_items.map((segmentItem) => {
            if (items.map(({ item_id }) => item_id).includes(segmentItem.item_id)) {
              const item = items.find(({ item_id }) => item_id === segmentItem.item_id);

              if (item?.type === 'product') {
                return {
                  ...segmentItem,
                  product_id: item.product_id,
                  product_price_id: item.product_price_id,
                };
              }

              if (item?.type === 'value') {
                return {
                  ...segmentItem,
                  value: item.value,
                };
              }

              if (item?.type === 'rule') {
                return {
                  ...segmentItem,
                  rule_id: item.rule_id,
                };
              }
            }

            return segmentItem;
          });

          return {
            ...segment,
            calculation_segment_items: calculationSegmentItems,
          };
        }

        return segment;
      });

      return {
        ...currentCalculation,
        calculation_segments: updatedSegments,
      };
    }

    function saveCalculation(calculation: Calculation) {
      data.value =
        data.value?.map((calc) => {
          if (calc.uuid === calculation.uuid) {
            return calculation;
          }

          return calc;
        }) || null;
    }

    function removeCalculationById(id: number) {
      if (!data.value) return;

      const idsToRemove = getDescendantCalculationIds(id, data.value);

      data.value = data.value.filter((calculation) => {
        return calculation.id === null || !idsToRemove.has(calculation.id);
      });
    }

    function appendCalculationChild(_parentId: number, child: Calculation) {
      if (!data.value) return;

      data.value = [...data.value, child];
    }

    function getDescendantCalculationIds(calculationId: number, calculations: Calculation[]) {
      const idsToRemove = new Set<number>([calculationId]);
      const queue = [calculationId];

      while (queue.length > 0) {
        const currentId = queue.shift();

        calculations.forEach((calculation) => {
          if ((calculation as any).parent_id === currentId && calculation.id !== null) {
            if (!idsToRemove.has(calculation.id)) {
              idsToRemove.add(calculation.id);
              queue.push(calculation.id);
            }
          }
        });
      }

      return idsToRemove;
    }

    function deleteCalculationSegment(calculationUuid: string, segmentId: number) {
      if (!data.value) return null;

      data.value = data.value.map((calculation) => {
        if (calculation.uuid === calculationUuid) {
          return {
            ...calculation,
            calculation_segments: calculation.calculation_segments.map((segment) => {
              if (segment.segment_id !== segmentId) {
                return segment;
              }

              return {
                ...segment,
                is_deleted: true,
              };
            }),
          };
        }

        return calculation;
      });

      return data.value.find((calculation) => calculation.uuid === calculationUuid);
    }

    function getLastClientCalculation(
      calculationUuid: string,
      clientUuid: string,
      calculatorId?: number
    ) {
      if (!data.value) return null;

      const calculations = data.value
        .filter((calculation) => calculation.client_uuid === clientUuid)
        .filter((calculation) => calculation.uuid !== calculationUuid)
        .filter((calculation) => (calculation as any).parent_id == null)
        .filter((calculation) => {
          if (calculatorId == null || Number.isNaN(calculatorId)) {
            return true;
          }

          return calculation.calculator_id === calculatorId;
        })
        .sort((left, right) => {
          return (
            new Date(right.calculation_created_at).getTime() -
            new Date(left.calculation_created_at).getTime()
          );
        });

      return calculations[0] || null;
    }

    return {
      data,
      fetch,
      invalidate,
      addNewCalculation,
      addSegmentToCalculation,
      getCalculationByUuid,
      saveCalculation,
      getUpdatedItemsCalculationSegment,
      appendCalculation,
      appendCalculationChild,
      removeCalculationById,
      deleteCalculationSegment,
      getLastClientCalculation,
      copySegmentToCalculation,
    };
  },
  {
    // @ts-ignore
    persist: true,
  }
);
